import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { TextInput, Button, Text, Surface } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import RNPickerSelect from 'react-native-picker-select';
import { Formik } from 'formik';
import * as Yup from 'yup';
import authService from '../services/authService';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import { brandPrimary, brandButton } from '../theme/paperTheme';

type RootStackParamList = {
  Landing: undefined;
  Login: undefined;
  Register: undefined;
  Home: undefined;
};

type RegisterScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Register'>;
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
  gradeLevel: Yup.string().required('Grade level is required'),
});

const pickerSelectStyles = {
  inputIOS: {
    color: '#1E293B',
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.12)',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
  },
  inputAndroid: {
    color: '#1E293B',
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.12)',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
  },
  inputWeb: {
    color: '#1E293B',
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.12)',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
  },
  placeholder: {
    color: '#94A3B8',
  },
  viewContainer: {
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.12)',
    borderRadius: 12,
    marginBottom: 12,
    backgroundColor: '#FFFFFF',
  },
};

const inputOutline = {
  outlineColor: 'rgba(15, 23, 42, 0.12)',
  activeOutlineColor: brandPrimary,
};

const RegisterScreen: React.FC<RegisterScreenProps> = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showVerificationNotice, setShowVerificationNotice] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleRegister = async (values: {
    name: string;
    email: string;
    password: string;
    gradeLevel: string;
    confirmPassword: string;
  }) => {
    try {
      setError('');
      setLoading(true);
      await authService.register(values.name, values.email, values.password, values.gradeLevel);
      setShowVerificationNotice(true);
      setShowSuccess(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to register';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Surface style={styles.surface}>
          <View style={styles.headerWrap}>
            <HeaderBar
              buttonLabel="Login"
              onButtonPress={() => navigation.navigate('Login')}
              onLogoPress={() => navigation.navigate('Landing')}
              style={styles.headerBar}
            />
          </View>

          <MaxWidthContainer style={[styles.formContainer, isDesktop && styles.wideContainer]}>
            <View style={styles.heroSection}>
              <Text style={styles.screenLabel}>Join the community</Text>
              <Text style={styles.title}>Create your account</Text>
              <Text style={styles.subtitle}>
                Connect with fellow educators and get the support you need.
              </Text>
            </View>

            <Surface style={styles.formCard} elevation={0}>
              {error ? <Text style={styles.error}>{error}</Text> : null}

              <Formik
                initialValues={{
                  name: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                  gradeLevel: '',
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, actions) => {
                  try {
                    await handleRegister(values);
                    actions.resetForm();
                  } catch {
                    // handled in handleRegister
                  }
                }}
              >
                {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setFieldValue }) => (
                  <View>
                    <TextInput
                      label="Full name"
                      value={values.name}
                      onChangeText={handleChange('name')}
                      onBlur={handleBlur('name')}
                      mode="outlined"
                      style={styles.input}
                      error={touched.name && !!errors.name}
                      {...inputOutline}
                    />
                    {touched.name && errors.name ? (
                      <Text style={styles.validationError}>{errors.name}</Text>
                    ) : null}

                    <TextInput
                      label="Email"
                      value={values.email}
                      onChangeText={handleChange('email')}
                      onBlur={handleBlur('email')}
                      mode="outlined"
                      style={styles.input}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      error={touched.email && !!errors.email}
                      {...inputOutline}
                    />
                    {touched.email && errors.email ? (
                      <Text style={styles.validationError}>{errors.email}</Text>
                    ) : null}

                    <TextInput
                      label="Password"
                      value={values.password}
                      onChangeText={handleChange('password')}
                      onBlur={handleBlur('password')}
                      mode="outlined"
                      style={styles.input}
                      secureTextEntry
                      error={touched.password && !!errors.password}
                      {...inputOutline}
                    />
                    {touched.password && errors.password ? (
                      <Text style={styles.validationError}>{errors.password}</Text>
                    ) : null}

                    <TextInput
                      label="Confirm password"
                      value={values.confirmPassword}
                      onChangeText={handleChange('confirmPassword')}
                      onBlur={handleBlur('confirmPassword')}
                      mode="outlined"
                      style={styles.input}
                      secureTextEntry
                      error={touched.confirmPassword && !!errors.confirmPassword}
                      {...inputOutline}
                    />
                    {touched.confirmPassword && errors.confirmPassword ? (
                      <Text style={styles.validationError}>{errors.confirmPassword}</Text>
                    ) : null}

                    <Text style={styles.fieldLabel}>Grade level</Text>
                    <RNPickerSelect
                      onValueChange={(value) => setFieldValue('gradeLevel', value)}
                      items={[
                        { label: 'Kindergarten', value: 'K' },
                        { label: '1st Grade', value: '1' },
                        { label: '2nd Grade', value: '2' },
                        { label: '3rd Grade', value: '3' },
                        { label: '4th Grade', value: '4' },
                        { label: '5th Grade', value: '5' },
                        { label: '6th Grade', value: '6' },
                        { label: '7th Grade', value: '7' },
                        { label: '8th Grade', value: '8' },
                        { label: '9th Grade', value: '9' },
                        { label: '10th Grade', value: '10' },
                        { label: '11th Grade', value: '11' },
                        { label: '12th Grade', value: '12' },
                        { label: 'Other', value: 'Other' },
                      ]}
                      placeholder={{ label: 'Select grade level', value: '' }}
                      value={values.gradeLevel}
                      style={pickerSelectStyles}
                      useNativeAndroidPickerStyle={false}
                    />

                    {touched.gradeLevel && errors.gradeLevel ? (
                      <Text style={styles.validationError}>{errors.gradeLevel}</Text>
                    ) : null}

                    <Button
                      mode="contained"
                      onPress={() => handleSubmit()}
                      loading={loading}
                      style={styles.primaryButton}
                      buttonColor={brandButton}
                      textColor="#ffffff"
                      disabled={loading || showSuccess}
                    >
                      Create account
                    </Button>

                    {showVerificationNotice ? (
                      <View style={styles.verificationBlock}>
                        <Text style={styles.verificationNotice}>
                          Registration successful! Please check your email to verify your account.
                        </Text>
                        <Text style={styles.spamNotice}>
                          If you don't see the email, please check your spam or junk folder.
                        </Text>
                        <Button
                          mode="text"
                          onPress={async () => {
                            setResendMessage('');
                            setResendError('');
                            try {
                              if (!values.email) {
                                setResendError('Please enter your email above first.');
                                return;
                              }
                              const result = await authService.resendVerificationEmail(values.email);
                              setResendMessage(result.message);
                            } catch (err: unknown) {
                              const message =
                                err instanceof Error
                                  ? err.message
                                  : 'Failed to resend verification email';
                              setResendError(message);
                            }
                          }}
                          style={styles.linkButton}
                          textColor={brandPrimary}
                        >
                          Resend verification email
                        </Button>
                        {resendMessage ? (
                          <Text style={styles.successMessage}>{resendMessage}</Text>
                        ) : null}
                        {resendError ? <Text style={styles.error}>{resendError}</Text> : null}
                        <Button
                          mode="text"
                          onPress={() => navigation.navigate('Landing')}
                          style={styles.linkButton}
                          textColor={brandPrimary}
                        >
                          Back to lesson generator
                        </Button>
                      </View>
                    ) : null}

                    <Button
                      mode="text"
                      onPress={() => navigation.navigate('Login')}
                      style={styles.linkButton}
                      textColor={brandPrimary}
                    >
                      Already have an account? Log in
                    </Button>
                  </View>
                )}
              </Formik>
            </Surface>
          </MaxWidthContainer>
        </Surface>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  formContainer: {
    maxWidth: 560,
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  wideContainer: {
    maxWidth: 480,
  },
  heroSection: {
    marginBottom: 24,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
    textAlign: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
    color: '#1E293B',
    letterSpacing: -0.5,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginBottom: 8,
    marginLeft: 4,
  },
  input: {
    marginBottom: 12,
    backgroundColor: '#FFFFFF',
  },
  primaryButton: {
    marginTop: 8,
    borderRadius: 999,
    paddingVertical: 4,
  },
  linkButton: {
    marginTop: 4,
  },
  error: {
    color: '#d32f2f',
    textAlign: 'center',
    marginBottom: 14,
    fontSize: 15,
    fontWeight: '500',
  },
  validationError: {
    color: '#d32f2f',
    fontSize: 12,
    marginTop: -6,
    marginBottom: 10,
    marginLeft: 4,
  },
  verificationBlock: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(15, 23, 42, 0.06)',
  },
  successMessage: {
    color: brandPrimary,
    textAlign: 'center',
    marginTop: 8,
    fontSize: 15,
    fontWeight: '600',
  },
  verificationNotice: {
    color: brandPrimary,
    textAlign: 'center',
    marginTop: 4,
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 22,
  },
  spamNotice: {
    color: '#64748B',
    textAlign: 'center',
    marginTop: 10,
    fontSize: 14,
    lineHeight: 20,
  },
});

export default RegisterScreen;
